import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Briefcase, CalendarCheck, Plus } from 'lucide-react';
import { crmWorkApi, type CrmWorkView } from '../../api/crm';
import { useCompany } from '../../contexts/CompanyContext';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import CrmActivityItem from '../../components/crm/CrmActivityItem';
import CrmActivityForm from '../../components/crm/CrmActivityForm';
import { formatMoney, relativeTime } from '../../lib/crmInbox';
import type { CrmWorkCounts } from '../../types';

// Two questions, one page: what is on my plate today, and which opportunities
// have nothing decided for them. Both read the same records the detail pages
// show — this is a lens, not another to-do list.
const VIEWS: { id: CrmWorkView; label: string; help: string; count: keyof CrmWorkCounts | null; group: 'work' | 'deals' }[] = [
  { id: 'today', label: 'Due today', help: 'Calls, meetings and tasks scheduled for today.', count: 'today', group: 'work' },
  { id: 'overdue', label: 'Overdue', help: 'Scheduled work whose time has passed.', count: 'overdue', group: 'work' },
  { id: 'upcoming', label: 'This week', help: 'Scheduled for the next seven days.', count: 'upcoming', group: 'work' },
  { id: 'unscheduled', label: 'No date', help: 'Open work nobody has put a date on.', count: 'unscheduled', group: 'work' },
  { id: 'open', label: 'All open', help: 'Everything still open, dated or not.', count: null, group: 'work' },
  { id: 'deals-next-due', label: 'Next step due', help: 'Opportunities whose next step is due today or overdue.', count: 'nextDue', group: 'deals' },
  { id: 'deals-no-next-action', label: 'No next step', help: 'Open opportunities with nothing decided to do next.', count: 'noNextAction', group: 'deals' },
  { id: 'deals-stale', label: 'Gone quiet', help: 'Open opportunities untouched for two weeks.', count: 'stale', group: 'deals' },
];

export default function Work() {
  const { currentCompany } = useCompany();
  const queryClient = useQueryClient();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const [mine, setMine] = useState(true);
  const [view, setView] = useState<CrmWorkView>('today');
  const [logging, setLogging] = useState(false);

  const { data: counts } = useQuery({
    queryKey: ['crm-work-counts', cid, mine],
    queryFn: () => crmWorkApi.counts(mine),
    enabled: !!cid,
  });
  const { data, isLoading } = useQuery({
    queryKey: ['crm-work', cid, view, mine],
    queryFn: () => crmWorkApi.list(view, mine),
    enabled: !!cid,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['crm-work'] });
    queryClient.invalidateQueries({ queryKey: ['crm-work-counts'] });
  };

  const meta = VIEWS.find(v => v.id === view)!;
  const activities = data?.activities ?? [];
  const deals = data?.deals ?? [];
  const total = (counts?.today ?? 0) + (counts?.overdue ?? 0);

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="My work"
        intro={
          counts
            ? `${counts.overdue} overdue, ${counts.today} due today. Below that: opportunities that need a decision about what happens next.`
            : 'Everything on your plate, and the opportunities that need a next step.'
        }
        actions={
          <>
            <div className="flex rounded-lg border border-line overflow-hidden" role="group" aria-label="Whose work">
              {[{ v: true, l: 'Mine' }, { v: false, l: 'Everyone' }].map(o => (
                <button
                  key={o.l}
                  type="button"
                  onClick={() => setMine(o.v)}
                  className={`px-3 py-1.5 text-xs font-semibold ${mine === o.v ? 'bg-primary text-white' : 'bg-surface text-muted hover:text-fg'}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            <button type="button" className="btn btn-primary inline-flex items-center gap-1.5" onClick={() => setLogging(true)}>
              <Plus className="w-4 h-4" /> Log / schedule
            </button>
          </>
        }
      />

      <div className="flex flex-wrap gap-2">
        {VIEWS.map(v => {
          const n = v.count && counts ? counts[v.count] : undefined;
          const on = view === v.id;
          const urgent = (v.id === 'overdue' || v.id === 'deals-next-due') && !!n;
          return (
            <button
              key={v.id}
              type="button"
              title={v.help}
              onClick={() => setView(v.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                on ? 'bg-primary text-white border-primary' : 'bg-surface text-muted border-line hover:text-fg'
              } ${v.group === 'deals' && !on ? 'border-dashed' : ''}`}
            >
              {v.label}
              {n !== undefined && n > 0 && (
                <span className={`ml-1.5 ${on ? 'opacity-90' : urgent ? 'text-err' : 'text-fg'}`}>{n}</span>
              )}
            </button>
          );
        })}
      </div>

      <p className="text-[13px] text-muted">{meta.help}</p>

      {isLoading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="card h-16 animate-pulse" />)}</div>
      ) : meta.group === 'work' ? (
        activities.length === 0 ? (
          <EmptyState icon={<CalendarCheck className="w-8 h-8" />}>
            {view === 'overdue' ? 'Nothing is overdue.' : view === 'today' ? 'Nothing scheduled for today.' : 'Nothing here.'}
          </EmptyState>
        ) : (
          <div className="space-y-2">
            {activities.map(a => <CrmActivityItem key={a.id} activity={a as any} onUpdated={refresh} />)}
          </div>
        )
      ) : deals.length === 0 ? (
        <EmptyState icon={<Briefcase className="w-8 h-8" />}>
          {view === 'deals-no-next-action' ? 'Every open opportunity has a next step. Good.' : 'Nothing here.'}
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {deals.map(d => {
            const due = d.nextActionDate ? new Date(d.nextActionDate) : null;
            const overdue = !!due && due.getTime() < Date.now();
            return (
              <Link key={d.id} to={`/crm/deals/${d.id}`} className="card flex items-start justify-between gap-3 hover:bg-row-odd transition-colors">
                <div className="min-w-0">
                  <p className="font-medium text-sm text-fg">{d.title}</p>
                  <p className="text-xs text-muted flex flex-wrap items-center gap-1.5">
                    {d.stage && <><span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.stage.color }} />{d.stage.name}</>}
                    {d.organization && <span>· {d.organization.name}</span>}
                    {d.owner && <span>· {d.owner.name}</span>}
                    <span>· touched {relativeTime((d as any).updatedAt)}</span>
                  </p>
                  <p className={`text-xs mt-1 ${d.nextAction ? (overdue ? 'text-err' : 'text-fg') : 'text-warn'}`}>
                    {d.nextAction
                      ? `Next: ${d.nextAction}${due ? ` · ${overdue ? 'was due' : 'due'} ${due.toLocaleDateString()}` : ''}${d.nextActionOwner ? ` · ${d.nextActionOwner.name}` : ''}`
                      : 'No next step decided — open it and say what happens next'}
                  </p>
                </div>
                <p className="text-sm font-semibold text-fg whitespace-nowrap">{formatMoney(d.value, d.currency)}</p>
              </Link>
            );
          })}
        </div>
      )}

      {total === 0 && meta.group === 'work' && view === 'today' && (
        <p className="text-xs text-muted">Nothing scheduled. Use <b>Log / schedule</b> to put something on the calendar, or check the opportunity views above.</p>
      )}

      {logging && (
        <CrmActivityForm onClose={() => setLogging(false)} onSaved={() => { setLogging(false); refresh(); }} />
      )}
    </div>
  );
}
